import type {Control, FieldPath, FieldValues} from 'react-hook-form'
import {Controller} from 'react-hook-form'
import {
  MossUIField,
  MossUIFieldLabel,
  MossUIMenuItem,
  MossUIMenuItemLabel,
  MossUISelect,
  MossUITextInput,
} from 'src/common/MossUIComponents'
import {useTranslate} from 'src/common/i18n/useTranslate'

export type RoleOption = {value: string; label: string}

export function LeadFormFields<T extends FieldValues = FieldValues>({
  control,
  roleOptions,
}: {
  control: Control<T>
  roleOptions: ReadonlyArray<RoleOption>
}) {
  const translate = useTranslate()

  const getRoleLabel = (role: RoleOption) => {
    const key = `leadForm.roles.${role.value}`
    const translated = translate(key)
    return translated === key ? role.label : translated
  }

  // Constraining T to require these 5 field names would need the shared useHubspotForm() hook's
  // FormData (Record<string, string>) to satisfy that shape too, which cascades into every one of
  // its other consumers. Left as a cast until that hook is typed per-caller.
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name={'firstname' as FieldPath<T>}
          control={control}
          render={({field: {value, onChange, onBlur}, fieldState}) => (
            <MossUIField error={fieldState.error?.message}>
              <MossUIFieldLabel>{translate('leadForm.firstName')}</MossUIFieldLabel>
              <MossUITextInput value={value} onChange={onChange} onBlur={onBlur} />
            </MossUIField>
          )}
        />
        <Controller
          name={'lastname' as FieldPath<T>}
          control={control}
          render={({field: {value, onChange, onBlur}, fieldState}) => (
            <MossUIField error={fieldState.error?.message}>
              <MossUIFieldLabel>{translate('leadForm.lastName')}</MossUIFieldLabel>
              <MossUITextInput value={value} onChange={onChange} onBlur={onBlur} />
            </MossUIField>
          )}
        />
      </div>

      <Controller
        name={'email' as FieldPath<T>}
        control={control}
        render={({field: {value, onChange, onBlur}, fieldState}) => (
          <MossUIField error={fieldState.error?.message}>
            <MossUIFieldLabel>{translate('leadForm.workEmail')}</MossUIFieldLabel>
            <MossUITextInput value={value} onChange={onChange} onBlur={onBlur} />
          </MossUIField>
        )}
      />

      <Controller
        name={'company' as FieldPath<T>}
        control={control}
        render={({field: {value, onChange, onBlur}, fieldState}) => (
          <MossUIField error={fieldState.error?.message}>
            <MossUIFieldLabel>{translate('leadForm.company')}</MossUIFieldLabel>
            <MossUITextInput value={value} onChange={onChange} onBlur={onBlur} />
          </MossUIField>
        )}
      />

      <Controller
        name={'hs_persona' as FieldPath<T>}
        control={control}
        rules={{required: translate('leadForm.roleRequired')}}
        render={({field, fieldState}) => {
          const selectedRole = roleOptions.find((role) => role.value === field.value)

          return (
            <MossUIField error={fieldState.error?.message}>
              <MossUIFieldLabel>{translate('leadForm.role')}</MossUIFieldLabel>
              <MossUISelect
                selectedItem={selectedRole && getRoleLabel(selectedRole)}
                placement="bottom-start"
                selectPlaceholder={translate('leadForm.rolePlaceholder')}
              >
                {roleOptions.map((option) => (
                  <MossUIMenuItem
                    key={option.value}
                    selected={option.value === field.value}
                    onClick={() => field.onChange(option.value)}
                  >
                    <MossUIMenuItemLabel textWrap>{getRoleLabel(option)}</MossUIMenuItemLabel>
                  </MossUIMenuItem>
                ))}
              </MossUISelect>
            </MossUIField>
          )
        }}
      />
    </>
  )
}
